import {
  Alert,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import IconButton from "../ui/IconButton";
import { LineChart } from "@mui/x-charts";
import { useEffect, useState, useCallback, useRef } from "react";
import { useIntl } from "../../contexts/IntlContext";
import { Download, Visibility } from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import MetricsTable from "./MetricsTable";
import html2canvas from "html2canvas";
import api from "../../services/api";
import { darkSurfaceColors } from "../../themes/darkSurfaceColors";

const MAX_DISPLAYED_METRICS = 32;

const sumSeries = (a: number[], b: number[]) =>
  a.map((value, index) => value + (b[index] ?? 0));

/**
 * Agrupa as séries por canal em uma por localização (cidade quando há cidades selecionadas,
 * senão estado), somando os valores. Seleções sem nenhum canal aparecem zeradas, para o usuário
 * ver "Paraíba: 0" em vez de a linha simplesmente sumir.
 */
const groupByLocation = (
  items: MetricData[],
  cities: string[],
  states: string[],
  length: number,
): { key: string; label: string; data: number[] }[] => {
  const byCity = cities.length > 0;
  const groups = new Map<string, { label: string; data: number[] }>();
  for (const item of items) {
    const place = byCity ? item.city : item.state;
    if (!place) continue;
    const key = place.toLowerCase() + (byCity ? `|${item.state.toLowerCase()}` : "");
    const label = byCity && item.state ? `${place} (${item.state})` : place;
    const current = groups.get(key);
    groups.set(key, {
      label,
      data: current ? sumSeries(current.data, item.data[1]) : [...item.data[1]],
    });
  }
  const selected = byCity ? cities : states;
  const present = new Set(
    Array.from(groups.keys()).map((key) => key.split("|")[0]),
  );
  for (const name of selected) {
    if (present.has(name.toLowerCase())) continue;
    groups.set(`missing|${name.toLowerCase()}`, {
      label: name,
      data: new Array(length).fill(0),
    });
  }
  return Array.from(groups.entries())
    .map(([key, group]) => ({ key, ...group }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
};

interface MetricData {
  channelId: string;
  channelName: string;
  establishmentName: string;
  country: string;
  state: string;
  city: string;
  metric: string;
  data: [string[], number[]];
}

interface ChartSeries {
  label: string;
  data: number[];
  color?: string;
}

interface MetricChartCardProps {
  title: string;
  metricName: string;
  granularity: "daily" | "monthly";
  startDate: string;
  endDate: string;
  serieLabelGenerator: (item: MetricData) => string;
  height?: number;
  vertical?: boolean;
  legendHeight?: number;
  aggregateTotal?: boolean;
  establishmentIds?: string[];
  channelIds?: string[];
  cities?: string[];
  states?: string[];
  showSeriesTable?: boolean;
  cardSx?: SxProps<Theme>;
  darkMode?: boolean;
  valueMultiplier?: number;
  displayMode?: "chart" | "table";
  refreshKey?: string;
  allowEmptyScope?: boolean;
}

const MetricChartCard = ({
  title,
  metricName,
  granularity,
  startDate,
  endDate,
  serieLabelGenerator,
  height = 400,
  vertical = false,
  legendHeight = 200,
  aggregateTotal,
  establishmentIds,
  channelIds,
  cities,
  states,
  showSeriesTable = true,
  cardSx,
  darkMode = false,
  valueMultiplier,
  displayMode = "chart",
  refreshKey,
  allowEmptyScope = false,
}: MetricChartCardProps) => {
  const intl = useIntl();
  const [data, setData] = useState<ChartSeries[]>([]);
  const [xLabels, setXLabels] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [selectedSeries, setSelectedSeries] = useState<string | null>(null);
  const [totalSeries, setTotalSeries] = useState<ChartSeries[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  const getColorFromUUID = useCallback((uuid: string) => {
    const defaultColors = [
      "#228B22",
      "#DC143C",
      "#1E90FF",
      "#FFD700",
      "#20B2AA",
      "#DA70D6",
      "#32CD32",
      "#B22222",
      "#4169E1",
      "#FFA500",
      "#008B8B",
      "#BA55D3",
      "#006400",
      "#8B0000",
      "#000080",
      "#FF8C00",
    ];

    let hash = 0;
    for (let i = 0; i < uuid.length; i++) {
      hash = uuid.charCodeAt(i) + ((hash << 5) - hash);
    }
    return defaultColors[Math.abs(hash) % defaultColors.length];
  }, []);

  const serieLabelGeneratorRef = useRef(serieLabelGenerator);
  const intlRef = useRef(intl);

  serieLabelGeneratorRef.current = serieLabelGenerator;
  intlRef.current = intl;

  const groupedByLocation = !!(cities?.length || states?.length);

  useEffect(() => {
    const fetchMetrics = async () => {
      setLoading(true);
      setHasError(false);
      try {
        const hasEstablishmentScope =
          establishmentIds && establishmentIds.length > 0;
        const hasChannelScope = channelIds && channelIds.length > 0;

        if (!allowEmptyScope && !hasEstablishmentScope && !hasChannelScope) {
          setData([]);
          setXLabels([]);
          setHasError(false);
          setLoading(false);
          return;
        }

        const params: any = {
          start: startDate,
          end: endDate,
          metric: metricName,
          granularity,
          // Com filtro de localização precisamos das séries por canal para somar por estado/cidade.
          aggregate_total: !!aggregateTotal && !groupedByLocation,
        };

        if (hasEstablishmentScope) {
          params.establishment_ids = establishmentIds!.join(",");
        }

        if (hasChannelScope) {
          params.channel_ids = channelIds!.join(",");
        }

        if (cities && cities.length > 0) {
          params.city = cities.join(",");
        }

        if (states && states.length > 0) {
          params.state = states.join(",");
        }

        if (refreshKey) {
          params.refresh_key = refreshKey;
        }

        const response = await api.get(`/app/analytics/report`, { params });

        const metricsData: MetricData[] = response.data;

        if (!Array.isArray(metricsData) || metricsData.length === 0) {
          setTotalSeries([]);
          setData([]);
          setXLabels([]);
          setHasError(false);
          return;
        }

        setXLabels(metricsData[0].data[0]);

        if (groupedByLocation) {
          const multiplier = valueMultiplier ?? 1;
          const groups = groupByLocation(
            metricsData,
            cities ?? [],
            states ?? [],
            metricsData[0].data[0].length,
          );
          const scaled = groups.map((group) => ({
            label: group.label,
            data: group.data.map((value) => value * multiplier),
            color: getColorFromUUID(group.key),
          }));
          setData(scaled);
          setTotalSeries(
            scaled.length > 1
              ? [
                  {
                    label: intlRef.current.formatMessage({ id: "metricChart.total" }),
                    data: scaled.reduce(
                      (acc, series) => sumSeries(acc, series.data),
                      new Array(scaled[0].data.length).fill(0),
                    ),
                    color: "#888888",
                  },
                ]
              : [],
          );
          return;
        }

        setTotalSeries([]);
        const series = metricsData.map((item, index) => ({
          label: serieLabelGeneratorRef.current(item),
          data: valueMultiplier
            ? item.data[1].map((value) => value * valueMultiplier)
            : item.data[1],
          color: getColorFromUUID(item.channelId || index.toString()),
        }));

        setData(series);
      } catch (error) {
        console.error(
          intlRef.current.formatMessage({ id: "metricChart.loadError" }),
          error,
        );
        setData([]);
        setXLabels([]);
        setHasError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [
    metricName,
    granularity,
    startDate,
    endDate,
    aggregateTotal,
    establishmentIds,
    channelIds,
    cities,
    states,
    groupedByLocation,
    refreshKey,
    allowEmptyScope,
    getColorFromUUID,
    valueMultiplier,
  ]);

  const displayData = data.slice(0, MAX_DISPLAYED_METRICS);
  const filteredData = selectedSeries
    ? displayData.filter((series) => series.label === selectedSeries)
    : displayData;

  // A linha de total só entra no gráfico (a tabela já tem o "Total Geral" no rodapé).
  const chartSeries = selectedSeries ? filteredData : [...filteredData, ...totalSeries];

  const total = data.reduce(
    (acc, series) => acc + series.data.reduce((sum, value) => sum + value, 0),
    0,
  );

  const handleRowClick = (seriesLabel: string) => {
    setSelectedSeries(selectedSeries === seriesLabel ? null : seriesLabel);
  };

  const lightTheme = createTheme({
    palette: {
      mode: "light",
      background: {
        default: "#ffffff",
        paper: "#ffffff",
      },
    },
  });

  const exportToPNG = async () => {
    setIsExporting(true);

    try {
      const startedAt = Date.now();
      const waitForExportRef = async () => {
        while (!exportRef.current) {
          if (Date.now() - startedAt > 2000) {
            throw new Error("Export container did not render in time");
          }
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      };

      await waitForExportRef();
      // Give the chart a couple frames to paint before snapshotting.
      await new Promise((resolve) =>
        requestAnimationFrame(() => resolve(null)),
      );
      await new Promise((resolve) =>
        requestAnimationFrame(() => resolve(null)),
      );

      const exportElement = exportRef.current;
      if (!exportElement) throw new Error("Export container is not available");

      const canvas = await html2canvas(exportElement, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });

      const link = document.createElement("a");
      const safeTitle = title.replace(/\s+/g, "_");
      const dateSuffix = endDate || new Date().toISOString().split("T")[0];
      link.download = `${safeTitle}_${dateSuffix}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const chartHeight = Math.max(height - 80, 200);
  const showLegendTable = showSeriesTable && displayMode !== "table";
  const locationHeader = groupedByLocation
    ? intl.formatMessage({
        id: cities?.length ? "dashboard.cityFilterLabel" : "dashboard.stateFilterLabel",
      })
    : undefined;
  const tableOnly = displayMode === "table";

  const baseCardStyles: SystemStyleObject<Theme> = {
    height: "100%",
    ...(darkMode
      ? {
          backgroundColor: darkSurfaceColors.background,
          border: `1px solid ${darkSurfaceColors.border}`,
          color: darkSurfaceColors.text,
        }
      : {}),
  };

  const combinedCardSx: SxProps<Theme> = cardSx
    ? Array.isArray(cardSx)
      ? [baseCardStyles, ...cardSx]
      : [baseCardStyles, cardSx]
    : baseCardStyles;

  return (
    <>
      <Card sx={combinedCardSx}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="h6">{title}</Typography>
            <IconButton
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              disabled={loading || isExporting}
              size="small"
              aria-label={intl.formatMessage({ id: "common.download" })}
            >
              <Download />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={!!menuAnchor}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  window.setTimeout(() => setViewDialogOpen(true), 0);
                }}
              >
                <Visibility sx={{ mr: 1, fontSize: 20 }} />
                {intl.formatMessage({ id: "common.view" })}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  void exportToPNG();
                }}
              >
                <Download sx={{ mr: 1, fontSize: 20 }} />
                {intl.formatMessage({ id: "common.download" })}
              </MenuItem>
            </Menu>
          </Box>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height={chartHeight}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: tableOnly ? "column" : vertical || !showLegendTable ? "column" : "row",
                gap: showLegendTable ? 2 : 0,
              }}
            >
              {!tableOnly && (
              <Box sx={{ flex: 1, minHeight: chartHeight }}>
                {filteredData.length === 0 || xLabels.length === 0 ? (
                  <Box
                    sx={{
                      height: chartHeight,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px dashed",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    {hasError ? (
                      <Alert severity="warning" sx={{ maxWidth: 360 }}>
                        {intl.formatMessage({ id: "metricChart.errorState" })}
                      </Alert>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {intl.formatMessage({ id: "metricChart.noData" })}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <LineChart
                    series={chartSeries.map((series) => ({
                      data: series.data,
                      label: series.label,
                      color: series.color,
                    }))}
                    xAxis={[{ scaleType: "point", data: xLabels }]}
                    height={chartHeight}
                    margin={{ left: 40, right: 30, top: 30, bottom: 30 }}
                    skipAnimation
                    slotProps={{
                      legend: { hidden: true },
                    }}
                  />
                )}
              </Box>
              )}
              {(showLegendTable || tableOnly) && (
                <MetricsTable
                  nameHeader={locationHeader}
                  data={data}
                  displayData={displayData}
                  total={total}
                  maxDisplayed={MAX_DISPLAYED_METRICS}
                  onRowClick={handleRowClick}
                  selectedSeries={selectedSeries}
                  hasError={hasError}
                  stickyHeader
                  stickyFooter
                  showOmittedMessage
                  showEmptyState
                  fillerRow
                  containerSx={{
                    width: tableOnly || vertical ? "100%" : "45%",
                    maxWidth: tableOnly || vertical ? "none" : "450px",
                    border: "1px solid",
                    borderRadius: 1,
                    height: tableOnly ? "auto" : (vertical ? legendHeight : chartHeight),
                    overflowY: "auto",
                  }}
                  tableSx={{ height: "100%" }}
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { backgroundColor: darkMode ? darkSurfaceColors.background : undefined } }}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {filteredData.length > 0 && xLabels.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 1 }}>
              <Box sx={{ minHeight: 300 }}>
                <LineChart
                  series={chartSeries.map((series) => ({
                    data: series.data,
                    label: series.label,
                    color: series.color,
                  }))}
                  xAxis={[{ scaleType: "point", data: xLabels }]}
                  height={300}
                  margin={{ left: 40, right: 30, top: 30, bottom: 30 }}
                  skipAnimation
                  slotProps={{
                    legend: { hidden: true },
                  }}
                />
              </Box>
              <MetricsTable
                  nameHeader={locationHeader}
                data={data}
                displayData={displayData}
                total={total}
                containerSx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              />
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary", py: 2 }}>
              {intl.formatMessage({ id: "metricChart.noData" })}
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {isExporting && (
        <Box
          sx={{
            position: "fixed",
            left: "-100vw",
            top: 0,
            width: "100vw",
            height: "100vh",
            overflow: "hidden",
            zIndex: -9999,
          }}
        >
          <ThemeProvider theme={lightTheme}>
            <Box
              ref={exportRef}
              sx={{
                width: "800px",
                backgroundColor: "white",
                p: 3,
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ color: "black" }}>
                {title}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <LineChart
                  series={[...displayData, ...totalSeries].map((series) => ({
                    data: series.data,
                    label: series.label,
                    color: series.color,
                  }))}
                  xAxis={[{ scaleType: "point", data: xLabels }]}
                  height={height - 80}
                  margin={{ left: 40, right: 30, top: 30, bottom: 30 }}
                  skipAnimation
                  slotProps={{
                    legend: { hidden: true },
                  }}
                />
              </Box>
              <MetricsTable
                  nameHeader={locationHeader}
                data={data}
                displayData={displayData}
                total={total}
                maxDisplayed={MAX_DISPLAYED_METRICS}
                showOmittedMessage
                containerSx={{ width: "100%", border: "1px solid", borderRadius: 1 }}
              />
            </Box>
          </ThemeProvider>
        </Box>
      )}
    </>
  );
};

export default MetricChartCard;
